import {Sparkles} from 'lucide-react';
import {type ComponentProps, type ReactNode} from 'react';

import {cn} from '../../lib/utils';
import {Icon} from '../Icon';
import {Badge} from '../shadcn/badge';

export type AiBadgeState = 'generating' | 'filled';

export type AiBadgeProps = Omit<ComponentProps<typeof Badge>, 'variant' | 'size' | 'dot'> & {
  /** Confidence score 0–1. ≥0.9 → success, 0.7–0.9 → warning, <0.7 → error. Ignored when `state` is set. */
  confidence?: number;
  /** `generating` → blue badge with sparkles; `filled` → green badge with a star. Falls back to the confidence-driven "AI" badge when omitted. */
  state?: AiBadgeState;
  children?: ReactNode;
};

function confidenceVariant(confidence: number | undefined): 'success' | 'warning' | 'error' {
  if (confidence === undefined || confidence >= 0.9) return 'success';
  if (confidence >= 0.7) return 'warning';
  return 'error';
}

export function AiBadge({className, confidence, state, children, ...props}: AiBadgeProps) {
  if (state === 'generating') {
    return (
      <Badge variant="info" size="sm" className={cn('px-2', className)} {...props}>
        {children}
        <Sparkles className="size-3.5 shrink-0" />
      </Badge>
    );
  }

  if (state === 'filled') {
    return (
      <Badge variant="success" size="sm" className={cn('px-2', className)} {...props}>
        {children}
        <Icon name="star" className="size-3.5 shrink-0" />
      </Badge>
    );
  }

  return (
    <Badge
      variant={confidenceVariant(confidence)}
      size="sm"
      className={cn('px-1', className)}
      {...props}
    >
      AI
      <Sparkles className="size-4 shrink-0" />
    </Badge>
  );
}
